import React from 'react';
import { Accordion } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  A11y,
  Autoplay,
  Keyboard,
  Navigation,
  Pagination,
} from 'swiper/modules';
import {
  FaGlobeAmericas,
  FaHandshake,
  FaHandsHelping,
  FaLeaf,
  FaShieldAlt,
  FaUsers,
  FaLinkedin,
} from 'react-icons/fa';
import type { IconBaseProps, IconType } from 'react-icons';
import Header from '../../components/layout/Header';
import AboutFeedback from '../../components/ui/AboutFeedback';
import team1 from '../../assets/img/team1.webp';
import team2 from '../../assets/img/team2.webp';
import team3 from '../../assets/img/team3.webp';
import team4 from '../../assets/img/team4.webp';
import team6 from '../../assets/img/team6.webp';
import swissFlagIcon from '../../assets/img/ch.svg';
import brazilFlagIcon from '../../assets/img/br.svg';
import austriaFlagIcon from '../../assets/img/at.svg';
import '../../styles/About.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const ClientMap = React.lazy(() => import('../../components/ui/ClientMap'));

/**
 * Reserves the map's box (the SVG is 960x620 and `width: 100%`), so mounting
 * the real map later cannot shift anything.
 */
const MapPlaceholder = () => (
  <div className="about-map-placeholder" aria-hidden="true" />
);

/**
 * Mounts the map only once it is near the viewport. It lives ~990px down the
 * page, and drawing its 181 country paths up front was the single biggest
 * source of main-thread work on this route.
 */
const ClientMapWhenNear = () => {
  const slotRef = React.useRef<HTMLDivElement>(null);
  const [shouldMount, setShouldMount] = React.useState(false);

  React.useEffect(() => {
    if (shouldMount) return;

    // During pre-rendering the map would mount (it falls inside the look-ahead
    // margin) and 180 country paths would be baked into the shipped HTML.
    // Real visitors never have this flag, so they still get the observer.
    if ((window as { __PRERENDER__?: boolean }).__PRERENDER__) return;

    const slot = slotRef.current;
    if (!slot || typeof IntersectionObserver === 'undefined') {
      setShouldMount(true); // no observer support: keep the old behaviour
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }, // start loading before it is actually reached
    );

    observer.observe(slot);
    return () => observer.disconnect();
  }, [shouldMount]);

  return (
    <div ref={slotRef} className="about-map-slot">
      {shouldMount ? (
        <React.Suspense fallback={<MapPlaceholder />}>
          <ClientMap />
        </React.Suspense>
      ) : (
        <MapPlaceholder />
      )}
    </div>
  );
};

const asIconComponent = (Icon: IconType, name: string) => {
  const Comp: React.FC<IconBaseProps> = (props) => {
    const element = Icon(props);
    return element as React.ReactElement;
  };
  Comp.displayName = name;
  return Comp;
};

const ValueIcons = {
  equity: asIconComponent(FaHandsHelping, 'EquityIcon'),
  inclusion: asIconComponent(FaGlobeAmericas, 'InclusionIcon'),
  integrity: asIconComponent(FaShieldAlt, 'IntegrityIcon'),
  collaboration: asIconComponent(FaHandshake, 'CollaborationIcon'),
  sustainability: asIconComponent(FaLeaf, 'SustainabilityIcon'),
};

const StoryTeamIcon = asIconComponent(FaUsers, 'StoryTeamIcon');
const LinkedinIcon = asIconComponent(FaLinkedin, 'LinkedinIcon');

const aboutTeamPhotos = [team1, team2, team3, team4, team6];
const aboutTeamFlags = [
  swissFlagIcon,
  brazilFlagIcon,
  brazilFlagIcon,
  brazilFlagIcon,
  austriaFlagIcon,
];
const aboutTeamLinkedin = [
  'https://www.linkedin.com/in/daniel-hefti-8156a3299/',
  'https://www.linkedin.com/in/igorgabriel-dev/',
  'https://www.linkedin.com/in/vihhllopes/',
  'https://www.linkedin.com/in/matheus-luiz-001334201/',
  'https://www.linkedin.com/in/laryssa-sams-8a81163a0/?locale=de',
];

const AboutPage = () => {
  const { t } = useTranslation();
  const valueKeys = [
    'equity',
    'inclusion',
    'integrity',
    'sustainability',
    'collaboration',
  ] as const;
  const websiteSlides = t('about.website_carousel.items', {
    returnObjects: true,
  }) as Array<{ title: string; description: string }>;
  const teamMembers = t('about.team.members', {
    returnObjects: true,
  }) as Array<{ name: string; role: string; description: string }>;
  const faqItems = t('about.faq.items', {
    returnObjects: true,
  }) as Array<{ question: string; answer: string }>;
  return (
    <>
      <Header />

      <main className="about-page">
        <section className="about-hero">
          <img
            className="about-hero-image"
            src="/banner-about.webp"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
          />
          <div className="about-hero-overlay" aria-hidden="true" />
          <div className="about-hero-content">
            <h1>
              {t('about.hero_title_line1')}
              <span>{t('about.hero_title_line2')}</span>
            </h1>
            <span className="about-hero-rule" aria-hidden="true" />
            <p>{t('about.hero_tagline')}</p>
          </div>
        </section>

        <section className="about-section about-story">
          <div className="about-story-copy">
            <div className="about-section-heading">
              <h2>
                {t('about.story_title_part1')}
                <strong>{t('about.story_title_part2')}</strong>
              </h2>
            </div>
            <div className="about-copy">
              {(
                t('about.story_paragraphs', {
                  returnObjects: true,
                }) as string[]
              ).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>

            <div className="about-story-highlight">
              <StoryTeamIcon aria-hidden="true" />
              <p>
                <Trans
                  i18nKey="about.story_highlight"
                  components={{ strong: <strong key="story-highlight" /> }}
                />
              </p>
            </div>
          </div>

          <div className="about-story-side">
            <div
              className="about-client-map"
              aria-label={t('about.client_map_title')}
            >
              <div className="about-map-header">
                <span>{t('about.client_map_title')}</span>
                <strong>{t('about.client_map_country')}</strong>
              </div>
              <ClientMapWhenNear />
            </div>
          </div>
          <aside className="about-story-quote">
            <p>
              <Trans
                i18nKey="about.story_quote"
                components={{ strong: <strong key="story-quote" /> }}
              />
            </p>
          </aside>
        </section>

        <section className="about-section about-website">
          <div className="about-section-heading about-section-heading-centered">
            <span>{t('about.website_carousel.label')}</span>
            <h2>{t('about.website_carousel.title')}</h2>
            <p>{t('about.website_carousel.subtitle')}</p>
          </div>

          <Swiper
            modules={[Navigation, Pagination, A11y, Autoplay, Keyboard]}
            navigation
            pagination={{ clickable: true }}
            keyboard={{ enabled: true }}
            autoplay={{ delay: 4200, disableOnInteraction: false }}
            speed={600}
            loop
            grabCursor
            breakpoints={{
              320: { slidesPerView: 1, spaceBetween: 16 },
              760: { slidesPerView: 2, spaceBetween: 20 },
              1100: { slidesPerView: 3, spaceBetween: 22 },
            }}
            className="about-website-swiper"
          >
            {websiteSlides.map((slide, index) => (
              <SwiperSlide key={slide.title}>
                <article className="about-website-card">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{slide.title}</h3>
                  <p>{slide.description}</p>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </section>

        <section className="about-section about-team">
          <div className="about-section-heading about-section-heading-centered">
            <span>{t('about.team.label')}</span>
            <h2>{t('about.team.title')}</h2>
            <p>{t('about.team.subtitle')}</p>
          </div>

          <div className="about-team-grid">
            {teamMembers.map((member, index) => (
              <article className="about-team-card" key={member.name}>
                <img
                  className="about-team-photo"
                  src={aboutTeamPhotos[index]}
                  alt={member.name}
                  loading="lazy"
                  decoding="async"
                />
                <img
                  className="about-team-flag"
                  src={aboutTeamFlags[index]}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                />
                <h3>{member.name}</h3>
                <span>{member.role}</span>
                <p>{member.description}</p>
                <a
                  href={aboutTeamLinkedin[index]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('about.team.linkedin_label', {
                    name: member.name,
                  })}
                >
                  <LinkedinIcon aria-hidden="true" />
                </a>
              </article>
            ))}
          </div>
        </section>

        <AboutFeedback />

        <section className="about-section">
          <div className="about-section-heading about-section-heading-centered">
            <h2>{t('about.values_title')}</h2>
            <p>{t('about.values_intro')}</p>
          </div>
          <div className="about-values">
            {valueKeys.map((key) => {
              const Icon = ValueIcons[key];

              return (
                <article className="about-value" key={key}>
                  <Icon className="about-value-icon" aria-hidden="true" />
                  <h3>{t(`about.values.${key}.title`)}</h3>
                  <p>{t(`about.values.${key}.text`)}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="about-section about-faq">
          <div className="about-section-heading about-section-heading-centered">
            <span>{t('about.faq.label')}</span>
            <h2>{t('about.faq.title')}</h2>
            <p>{t('about.faq.subtitle')}</p>
          </div>

          <Accordion className="about-faq-accordion" flush>
            {faqItems.map((item, index) => (
              <Accordion.Item eventKey={String(index)} key={item.question}>
                <Accordion.Header>{item.question}</Accordion.Header>
                <Accordion.Body>{item.answer}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </section>

        <section className="about-section about-final-cta">
          <div className="about-final-cta-content">
            <h2>
              {t('about.final_cta.title_line1')}
              <br />
              {t('about.final_cta.title_line2')}{' '}
              <span>{t('about.final_cta.title_accent')}</span>
            </h2>
          </div>

          <div className="about-final-cta-actions">
            <Link
              className="about-final-cta-button about-final-cta-button-primary"
              to="/contact"
            >
              {t('about.final_cta.contact_button')}
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutPage;
